/**
 * Monta as medidas da faixa de retorno a partir de totais atuais e do
 * periodo anterior. Uma funcao so para o hub e para a tela de WhatsApp: os
 * dois lugares chamam cada numero pelo mesmo nome e explicam do mesmo jeito.
 *
 * Decisoes nao obvias:
 *  1. Recuperado e a medida de DESTAQUE: e o que a empresa ganha e a base da
 *     comissao. Custo aparece, mas com sentido "neutro" (sobe com o volume);
 *     quem diz se foi bom e o custo por recuperacao ("menor e melhor").
 *  2. Custo por recuperacao sem pagamento nao e zero nem infinito: vira
 *     "—" com o motivo escrito. Com custo zero (so Evolution) vira "sem
 *     custo".
 */

import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../lib/format';
import { porUnidade, type Totais } from './comparar';
import type { MedidaDeRetorno } from './components/GradeDeRetorno';

export function medidasDeRetorno(atual: Totais, anterior: Totais | null, feeBps: number | null, escopo: 'whatsapp' | 'todos' = 'whatsapp'): readonly MedidaDeRetorno[] {
  const todos = escopo === 'todos';
  const unitario = porUnidade(atual.costCents, atual.paid);
  const unitarioAnterior = anterior === null ? null : porUnidade(anterior.costCents, anterior.paid);
  const semComparacaoUnitario =
    unitario === null
      ? atual.costCents === 0
        ? 'envios pelo número do comercial'
        : 'ainda sem pagamento no período'
      : anterior !== null && unitarioAnterior === null
        ? 'sem base no período anterior'
        : undefined;

  return [
    {
      chave: 'recuperado',
      rotulo: todos ? 'Recuperado pelos disparos' : 'Recuperado pelos envios',
      valor: formatBRLFromCents(atual.recoveredAmountCents),
      atual: atual.recoveredAmountCents,
      anterior: anterior === null ? null : anterior.recoveredAmountCents,
      tipo: 'dinheiro',
      apoio: todos ? 'WhatsApp e e-mail: pagamentos em até 7 dias com o disparo como último contato.' : 'Pagamentos em até 7 dias com o envio como último contato.',
      icone: 'moeda',
      destaque: true,
    },
    {
      chave: 'comissao',
      rotulo: 'Comissão',
      valor: formatBRLFromCents(atual.commissionCents),
      atual: atual.commissionCents,
      anterior: anterior === null ? null : anterior.commissionCents,
      tipo: 'dinheiro',
      apoio: feeBps === null ? 'O que o Converte Fácil fatura sobre o recuperado.' : `${formatPercentFromBps(feeBps)} sobre o recuperado: é o faturamento do Converte Fácil.`,
      icone: 'comissao',
    },
    {
      chave: 'pagaram',
      rotulo: 'Pagaram',
      valor: formatCount(atual.paid),
      atual: atual.paid,
      anterior: anterior === null ? null : anterior.paid,
      tipo: 'contagem',
      apoio: atual.paidViaSeller > 0 ? `${formatCount(atual.paidViaSeller)} fechados pela vendedora (fechado por); crédito do envio.` : 'Contatos que voltaram e pagaram.',
      icone: 'check',
    },
    {
      chave: 'respostas',
      rotulo: 'Respostas para o Comercial',
      valor: formatCount(atual.replied),
      atual: atual.replied,
      anterior: anterior === null ? null : anterior.replied,
      tipo: 'contagem',
      apoio: 'Caem com as vendedoras no WhatsApp do comercial.',
      icone: 'resposta',
    },
    {
      chave: 'custo',
      rotulo: todos ? 'Custo dos disparos' : 'Custo dos envios',
      valor: formatBRLFromCents(atual.costCents),
      atual: atual.costCents,
      anterior: anterior === null ? null : anterior.costCents,
      tipo: 'dinheiro',
      sentido: 'neutro',
      apoio: todos ? 'Tarifa da Meta e do provedor de e-mail; a Evolution não cobra por mensagem.' : 'Tarifa da Meta na API oficial; a Evolution não cobra por mensagem.',
      icone: 'grafico',
    },
    {
      chave: 'custo-por-recuperacao',
      rotulo: 'Custo por recuperação',
      valor: unitario === null ? (atual.costCents === 0 ? 'sem custo' : '—') : formatBRLFromCents(unitario),
      atual: unitario ?? 0,
      anterior: unitarioAnterior,
      tipo: 'dinheiro',
      sentido: 'menor_melhor',
      apoio: 'Custo dividido pelos pagamentos. Menor é melhor.',
      icone: 'raio',
      semComparacao: semComparacaoUnitario,
    },
  ];
}
